import React, { useState, useEffect } from 'react'
import { IconButton, CircularProgress } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import styled from '@emotion/styled'
import { alpha } from '@material-ui/core/styles'
import ReactMarkdown from 'react-markdown'
import FunctionChips from './FunctionChips'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import useChat from 'src/components/chatbot/hooks/useChat'

const Message = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: 12px;
  max-width: 80%;
  word-wrap: break-word;
`

// Mode comes from the theme; the isDarkMode prop is still passed but no longer read here.
const BotMessageContainer = styled(Message)`
  background-color: ${({ theme }) => theme.palette.background.neutral};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  color: ${({ theme }) => theme.palette.text.primary};
  align-self: flex-start;
`

const BotMessageContent = styled.div`
  & h2 {
    font-size: 1.2rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  & p {
    margin-bottom: 0.5rem;
  }
  & ul {
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
  }
  & ol {
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
  }
  & li {
    margin-bottom: 0.25rem;
  }
  & code {
    background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.08)};
    padding: 0.1rem 0.2rem;
    border-radius: 3px;
    font-family: ${({ theme }) => theme.typography.fontFamilyMono};
    font-size: 0.8125rem;
  }
  & pre {
    background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.08)};
    padding: 0.5rem;
    border-radius: 4px;
    overflow-x: auto;
    & code {
      background-color: transparent;
      padding: 0;
    }
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-left: ${({ theme }) => theme.spacing(1)};
`

// Reads as a contained primary button (the one action the user takes on a plan).
const ExecuteButton = styled(IconButton)`
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  border-radius: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(0.5, 1.5)};
  font-size: 0.8125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.dark};
  }

  &.Mui-disabled {
    background-color: ${({ theme }) => theme.palette.action.disabledBackground};
    border-color: transparent;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`

export default function BotMessage({ botMessage, isDarkMode, isLastMessage }) {
  const { isFilters, executeToolAction, chatConversationState } = useChatbotStore()
  const { handleToolAction } = useChat()
  const [tools, setTools] = useState(botMessage.tools || [])

  // Keep tools in sync if botMessage.tools changes (e.g. on new message)
  useEffect(() => {
    setTools(botMessage.tools || [])
  }, [botMessage.tools])

  return (
    <>
      <BotMessageContainer isDarkMode={isDarkMode}>
        <BotMessageContent isDarkMode={isDarkMode}>
          <ReactMarkdown>
            {botMessage.content.startsWith('\n        ## Execution Plan')
              ? botMessage.content.replace('\n        ## Execution Plan', '\n## Execution Plan')
              : botMessage.content}
          </ReactMarkdown>
        </BotMessageContent>
        {tools && tools.length > 0 && (
          <FunctionChips tools={tools} onToolsChange={setTools} isLastMessage={isLastMessage} />
        )}
      </BotMessageContainer>
      {isLastMessage &&
        tools &&
        (chatConversationState === 'tool_action_pending_success' ||
          chatConversationState === 'genesus_filter_planning') && (
          <ActionButtons>
            <ExecuteButton
              color="primary"
              onClick={() => handleToolAction(isFilters, tools)}
              disabled={executeToolAction}
              size="small"
            >
              {executeToolAction ? (
                <>
                  <CircularProgress size={16} />
                  <span>Executing...</span>
                </>
              ) : (
                'Accept'
              )}
            </ExecuteButton>
          </ActionButtons>
        )}
    </>
  )
}
